// src/components/PODocument.tsx
// HP2 WIRED — company branding (name, ABN, address, accent colour, logo, font)
// now reads from documentBranding.ts via getDocumentBranding().
//
// Architecture:
//   • This component renders the modal PREVIEW using React + branding data.
//   • The "Print / Save PDF" button delegates to printPODocument() (poPdfService.ts),
//     which also reads getDocumentBranding() and opens the system print dialog.
//     This ensures the modal preview and the printed output are always in sync.

import { useEffect } from 'react'
import { X, Printer } from 'lucide-react'
import { getDocumentBranding } from '../utils/documentBranding'
import { printPODocument } from '../services/poPdfService'

// ── Helpers ───────────────────────────────────────────────────────

function fmtAUD(n: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(n)
}
function fmtDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
}
function due30(iso?: string) {
  if (!iso) return '—'
  const d = new Date(iso)
  d.setDate(d.getDate() + 30)
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
}

// ── PO Document component ─────────────────────────────────────────

interface PODocumentProps {
  po: Record<string, any>
  job: Record<string, any> | null
  onClose: () => void
}

export function PODocument({ po, job, onClose }: PODocumentProps) {
  const b           = getDocumentBranding()
  const accent      = b.docTypeOverrides['PURCHASE_ORDER']?.accentColor ?? b.accentColor
  const poNumber    = po.poNumber ?? po.id ?? 'PO-XXXXXX'
  const totalExGST  = po.totalExGST ?? 0
  const gst         = totalExGST * 0.1
  const totalIncGST = totalExGST + gst
  const lineItems: any[] = po.lineItems ?? []
  const hasLineItems = lineItems.length > 0

  const addressLine = [b.address, b.suburb, b.state, b.postcode].filter(Boolean).join(', ')
  const contactLine = [
    b.showPhone && b.phone ? b.phone : null,
    b.showEmail && b.email ? b.email : null,
  ].filter(Boolean).join(' · ')

  // Close on Escape
  useEffect(() => {
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handle)
    return () => window.removeEventListener('keydown', handle)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col">

        {/* Modal toolbar */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50 rounded-t-2xl flex-shrink-0">
          <div>
            <div className="font-semibold text-gray-900">Purchase Order — {poNumber}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {po.supplier ?? 'Supplier not specified'} · {job?.jobNumber ?? ''} · {fmtDate(po.createdAt)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => printPODocument(po, job)}
              className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg text-white transition-colors"
              style={{ backgroundColor: accent }}
            >
              <Printer className="w-4 h-4" />Print / Save PDF
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable document preview */}
        <div className="overflow-y-auto p-6">
          <div
            className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm"
            style={{ fontFamily: `${b.fontFamily}, Arial, sans-serif`, fontSize: '10pt', color: '#1a1a1a' }}
          >
            {/* ── BRANDING HEADER ── */}
            <div style={{ borderBottom: `3px solid ${accent}`, paddingBottom: 14, marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                {b.logoBase64 ? (
                  <img src={b.logoBase64} alt={b.companyName} style={{ maxHeight: 60, maxWidth: 180, objectFit: 'contain' }} />
                ) : (
                  <div style={{ fontSize: 22, fontWeight: 900, color: accent, lineHeight: 1 }}>{b.companyName}</div>
                )}
                {b.showAbn     && <div style={{ fontSize: '8.5pt', color: '#6b7280', marginTop: 3 }}>ABN {b.abn}</div>}
                {b.showAddress && <div style={{ fontSize: '8.5pt', color: '#6b7280' }}>{addressLine}</div>}
                {contactLine   && <div style={{ fontSize: '8.5pt', color: '#6b7280' }}>{contactLine}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 17, fontWeight: 900, color: accent }}>PURCHASE ORDER</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: accent, marginTop: 3 }}>{poNumber}</div>
                <div style={{ fontSize: '8.5pt', color: '#6b7280', marginTop: 6, lineHeight: 1.7 }}>
                  Date issued: <strong>{fmtDate(po.createdAt)}</strong><br />
                  Payment due: <strong>{due30(po.createdAt)}</strong><br />
                  Status: <strong>{(po.status ?? '').replace(/_/g, ' ')}</strong>
                </div>
              </div>
            </div>

            {/* ── SUPPLIER + JOB REFERENCE ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 16 }}>
              <div style={{ border: `1.5px solid ${accent}`, borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ fontSize: '7.5pt', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: 8 }}>Supplier / vendor</div>
                <div style={{ fontSize: '12pt', fontWeight: 700, color: accent }}>{po.supplier ?? 'To be confirmed'}</div>
                {po.supplierABN     && <div style={{ fontSize: '8.5pt', color: '#6b7280', marginTop: 3 }}>ABN: {po.supplierABN}</div>}
                {po.supplierAddress && <div style={{ fontSize: '8.5pt', color: '#6b7280' }}>{po.supplierAddress}</div>}
                {po.supplierContact && <div style={{ fontSize: '8.5pt', color: '#6b7280' }}>Contact: {po.supplierContact}</div>}
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ fontSize: '7.5pt', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: 8 }}>Job / delivery reference</div>
                {job && <>
                  <div style={{ fontSize: '12pt', fontWeight: 700, color: '#111' }}>{job.jobNumber}</div>
                  <div style={{ fontSize: '9pt', color: '#6b7280', marginTop: 2 }}>{job.description ?? ''}</div>
                </>}
                {po.deliveryDate   && <div style={{ fontSize: '9pt', color: '#6b7280', marginTop: 6 }}>Required by: {fmtDate(po.deliveryDate)}</div>}
                {po.deliveryAddress && <div style={{ fontSize: '9pt', color: '#6b7280' }}>Deliver to: {po.deliveryAddress}</div>}
              </div>
            </div>

            {/* ── LINE ITEMS TABLE ── */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14 }}>
              <thead>
                <tr style={{ background: accent }}>
                  <th style={{ color: '#fff', padding: '7px 10px', fontSize: '9pt', textAlign: 'left', width: '50%' }}>Description</th>
                  <th style={{ color: '#fff', padding: '7px 8px', fontSize: '9pt', textAlign: 'right', width: '10%' }}>Qty</th>
                  <th style={{ color: '#fff', padding: '7px 8px', fontSize: '9pt', textAlign: 'left',  width: '10%' }}>Unit</th>
                  <th style={{ color: '#fff', padding: '7px 8px', fontSize: '9pt', textAlign: 'right', width: '15%' }}>Unit rate</th>
                  <th style={{ color: '#fff', padding: '7px 10px',fontSize: '9pt', textAlign: 'right', width: '15%' }}>Total ex GST</th>
                </tr>
              </thead>
              <tbody>
                {hasLineItems ? lineItems.map((item: any, i: number) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 500 }}>{item.description}</div>
                      {item.partNumber && <div style={{ fontSize: '8.5pt', color: '#6b7280' }}>Part no: {item.partNumber}</div>}
                    </td>
                    <td style={{ padding: '7px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontFamily: 'monospace' }}>{item.quantity ?? '—'}</td>
                    <td style={{ padding: '7px 8px', borderBottom: '1px solid #f3f4f6' }}>{item.unit ?? '—'}</td>
                    <td style={{ padding: '7px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontFamily: 'monospace' }}>{item.unitPrice != null ? fmtAUD(item.unitPrice) : '—'}</td>
                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtAUD(item.totalExGST ?? ((item.quantity ?? 0) * (item.unitPrice ?? 0)))}</td>
                  </tr>
                )) : (
                  <tr>
                    <td style={{ padding: '7px 10px', fontWeight: 500, lineHeight: 1.6 }}>{po.description}</td>
                    <td colSpan={4} style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, fontSize: '11pt' }}>{fmtAUD(totalExGST)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* ── TOTALS ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 18 }}>
              <div style={{ width: 260 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '10pt' }}>
                  <span style={{ color: '#6b7280' }}>Subtotal (ex GST)</span>
                  <span style={{ fontFamily: 'monospace' }}>{fmtAUD(totalExGST)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '10pt' }}>
                  <span style={{ color: '#6b7280' }}>GST (10%)</span>
                  <span style={{ fontFamily: 'monospace' }}>{fmtAUD(gst)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '12pt', fontWeight: 700, color: accent, borderTop: `2px solid ${accent}`, marginTop: 4 }}>
                  <span>TOTAL (inc GST)</span>
                  <span style={{ fontFamily: 'monospace' }}>{fmtAUD(totalIncGST)}</span>
                </div>
              </div>
            </div>

            {/* ── TERMS ── */}
            <div style={{ marginTop: 16, padding: '10px 12px', background: '#f4f7fc', borderRadius: 6, borderLeft: `4px solid ${accent}` }}>
              <p style={{ fontSize: '9pt', color: '#333', lineHeight: 1.5, marginBottom: 4 }}>
                <strong>Payment terms:</strong> 30 days from date of invoice.
              </p>
              <p style={{ fontSize: '9pt', color: '#333', lineHeight: 1.5, marginBottom: 4 }}>
                <strong>Delivery:</strong> Deliver to site address specified on this PO. Contact PM prior to delivery to confirm access.
              </p>
              <p style={{ fontSize: '9pt', color: '#333', lineHeight: 1.5 }}>
                <strong>Acceptance:</strong> Supply of goods or commencement of services constitutes acceptance of these terms.
              </p>
            </div>

            {/* ── SIGNATURE BLOCKS ── */}
            <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30 }}>
              <div>
                <div style={{ marginBottom: 40 }} />
                <div style={{ borderTop: '1px solid #999', paddingTop: 6, fontSize: '9pt', color: '#6b7280' }}>
                  Authorised signature — {b.companyName}<br />
                  Name: {po.authorisedByName ?? '________________________________'}<br />
                  Date: {po.authorisedAt ? fmtDate(po.authorisedAt) : '________________________________'}
                </div>
              </div>
              <div>
                <div style={{ marginBottom: 40 }} />
                <div style={{ borderTop: '1px solid #999', paddingTop: 6, fontSize: '9pt', color: '#6b7280' }}>
                  Supplier acknowledgement<br />
                  Name: ________________________________<br />
                  Date: ________________________________
                </div>
              </div>
            </div>

            {/* ── BRANDING FOOTER ── */}
            <div style={{ fontSize: '7.5pt', color: '#aaa', textAlign: 'center', marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
              {b.footerCustomText || `${b.companyName} · ABN ${b.abn}`}
              {b.showDocumentRef && ` · Ref: ${poNumber}`}
              {b.showGeneratedDate && ` · Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`}
              {b.showConfidential && ' · Confidential — for authorised recipients only'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
